import { useState } from 'react'
import { toast } from 'react-hot-toast'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../api/client'
import { getApiErrorMessage } from '../../../shared/utils/apiError'

// Renders and edits a task's custom field values (see
// apps/api/src/routes/goodday/custom-fields.ts). Only shows a section at all
// if the project has at least one custom field defined, so a task in a
// project with none configured looks exactly as it did before this existed.
interface CustomFieldValueRow {
  fieldId: string
  name: string
  type: 'TEXT' | 'NUMBER' | 'DATE' | 'SELECT' | 'BOOLEAN' | 'USER' | string
  config?: { options?: string[] }
  isRequired: boolean
  value: any
}

export function TaskCustomFieldsSection({ taskId }: { taskId: string | null }) {
  const queryClient = useQueryClient()
  const [drafts, setDrafts] = useState<Record<string, any>>({})

  const { data: fields = [], isLoading } = useQuery({
    queryKey: ['task-custom-field-values', taskId],
    queryFn: async () => {
      const res = await api.get<any>(`/tasks/${taskId}/custom-field-values`)
      return (res?.data || []) as CustomFieldValueRow[]
    },
    enabled: !!taskId,
  })

  const setValue = useMutation({
    mutationFn: ({ fieldId, value }: { fieldId: string; value: any }) =>
      api.put(`/tasks/${taskId}/custom-field-values`, { fieldId, value }, { skipErrorToast: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-custom-field-values', taskId] })
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'update this field')),
  })

  if (!taskId || isLoading || fields.length === 0) return null

  const draftFor = (f: CustomFieldValueRow) => (f.fieldId in drafts ? drafts[f.fieldId] : f.value ?? '')

  const commit = (f: CustomFieldValueRow, value: any) => {
    setValue.mutate({ fieldId: f.fieldId, value })
  }

  return (
    <div className="border-t border-gray-100 dark:border-slate-800/60 pt-4">
      <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-200 mb-3">Custom Fields</h3>
      <div className="space-y-3">
        {fields.map((f) => (
          <div key={f.fieldId} className="flex items-center justify-between gap-3">
            <label className="text-xs font-medium text-gray-600 dark:text-slate-400 shrink-0 w-32 truncate">
              {f.name}
              {f.isRequired && <span className="text-red-500">*</span>}
            </label>
            {f.type === 'BOOLEAN' ? (
              <input
                type="checkbox"
                checked={!!draftFor(f)}
                onChange={(e) => {
                  setDrafts((d) => ({ ...d, [f.fieldId]: e.target.checked }))
                  commit(f, e.target.checked)
                }}
                className="h-4 w-4"
              />
            ) : f.type === 'SELECT' ? (
              <select
                value={draftFor(f) || ''}
                onChange={(e) => {
                  setDrafts((d) => ({ ...d, [f.fieldId]: e.target.value }))
                  commit(f, e.target.value)
                }}
                className="flex-1 h-8 rounded-md border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 text-xs"
              >
                <option value="">—</option>
                {(f.config?.options || []).map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            ) : (
              <input
                type={f.type === 'NUMBER' ? 'number' : f.type === 'DATE' ? 'date' : 'text'}
                value={draftFor(f) ?? ''}
                onChange={(e) => setDrafts((d) => ({ ...d, [f.fieldId]: e.target.value }))}
                onBlur={(e) => commit(f, f.type === 'NUMBER' ? Number(e.target.value) : e.target.value)}
                className="flex-1 h-8 rounded-md border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 text-xs"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
